import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { addLocationsets, getLocationsetsItems } from "api/layer";
import { RightPanel } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { getLanguage } from "helpers/translations";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import UnresolvedLocationsDialog from "./unresolved-locations-dialog";

interface Props {
  readonly matchData: any[];
  readonly candidatesData: any[];
  readonly noMatchData: any[];
}

function AddressEncodingHeader({
  matchData,
  candidatesData,
  noMatchData
}: Props) {
  const [context, dispatch] = useContext(AppContext);
  const locationSets = context.locationSets;
  const [t] = useTranslation();
  const navigate = useNavigate();
  const importMultipleLocations = context.importMultipleLocations;
  const [dialogOpen, setDialogOpen] = useState(false);

  const backToImport = () => {
    navigate(-1);
  };

  const validateMatchData = (data: any[]) => {
    return data.map((item) => {
      const newItem = { ...item };
      for (const key in newItem) {
        if (Array.isArray(newItem[key]) && newItem[key].length === 0) {
          newItem[key] = null;
        }
      }
      return newItem;
    });
  };

  const uploadData = () => {
    const param = {
      name: importMultipleLocations?.name,
      locationSource: importMultipleLocations?.locationSource,
      aggregationUnit: importMultipleLocations?.aggregationUnit,
      comment: importMultipleLocations?.comment,
      items: validateMatchData(matchData)
    };

    addLocationsets(param)
      .then(async (result: any) => {
        if (result.code && result.code === 400) {
          dispatch({
            type: AppAction.addSnackBarParams,
            payload: {
              title: "tip",
              titleIconColor: "red",
              titleIcon: "error",
              resMessage:
                getLanguage() === "en"
                  ? result.data.messageEN
                  : result.data.messageCN
            }
          });

          dispatch({
            type: AppAction.setSingleLocationAnalysed,
            payload: false
          });
          dispatch({
            type: AppAction.setMultipleLocationsAnalysed,
            payload: false
          });
          return;
        }
        let isJson = true;
        if (typeof result !== "object") {
          try {
            result = JSON.parse(result);
          } catch (e) {
            console.error(e);
            isJson = false;
          }
        }
        if (!result.status || !isJson) {
          dispatch({
            type: AppAction.addSnackBarParams,
            payload: {
              titleIconColor: "yellow",
              titleIcon: "warning",
              title: "errorTitle",
              message: "errorMessage"
            }
          });
          return;
        }

        const locationSetId = result.data.id;
        getLocationsetsItems(locationSetId).then(async (res) => {
          let dataItem = res.data;
          dataItem.items = res.data.items.map((item: any) => {
            item.checked = true;
            return item;
          });
          if (locationSets) {
            dispatch({
              type: AppAction.setLocationSets,
              payload: [...locationSets, dataItem]
            });
          } else {
            dispatch({
              type: AppAction.setLocationSets,
              payload: [dataItem]
            });
          }
          dispatch({
            type: AppAction.setCurrentMultipleLocations,
            payload: dataItem
          });

          dispatch({
            type: AppAction.setSearchLocationInfo,
            payload: undefined
          });
          dispatch({
            type: AppAction.setSingleLocationAnalysed,
            payload: undefined
          });
          dispatch({
            type: AppAction.setMultipleLocationsAnalysed,
            payload: false
          });
          dispatch({
            type: AppAction.setRightPanel,
            payload: RightPanel.Multiple
          });

          dispatch({
            type: AppAction.addSnackBarParams,
            payload: {
              title: "importSuccessTitle",
              message: "importSuccessText"
            }
          });
        });
      })
      .catch((error) => {
        console.error(error);
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            titleIconColor: "yellow",
            titleIcon: "warning",
            title: "errorTitle",
            message: "errorMessage"
          }
        });
      })
      .finally(() => {
        dispatch({ type: AppAction.setEditModal, payload: undefined });
        navigate(-1);
      });
  };

  const complete = () => {
    const unresolvedCount = candidatesData.length + noMatchData.length;
    if (unresolvedCount > 0) {
      setDialogOpen(true);
    } else {
      uploadData();
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogConfirm = () => {
    setDialogOpen(false);
    uploadData();
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="space-between"
      alignItems="center"
    >
      <Box sx={{ fontSize: "19px" }}>{t("addressEncoding.title")}</Box>
      <Box>
        <Button
          color="success"
          onClick={backToImport}
          style={{ marginRight: "30px" }}
        >
          {t("buttons.backToImport")}
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={!matchData || matchData.length === 0}
          onClick={complete}
        >
          {t("buttons.complete")}
        </Button>
      </Box>
      <UnresolvedLocationsDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        unresolvedCount={candidatesData.length + noMatchData.length}
      />
    </Stack>
  );
}

export default AddressEncodingHeader;


